import type { AgentTaskModel } from "@fencyai/js";
import { useAgentTasks } from "@fencyai/react";
import { useCallback } from "react";
import type {
  AgentDocumentOwnerDto,
  AgentDocumentSearchScopeDto,
  AgentDocumentSearchSessionDto,
  AgentDocumentSourcesDto,
} from "../../autogen/bff-api";
import { AgentDocumentScopeTypeDto, useResolveAgentDocumentSourcesMutation } from "../../autogen/bff-api";
import type { DocumentSearchScope, DocumentSource, DocumentSourceOwner } from "./agentTypes";
import type { PageEntity, PageEntityKind } from "./pageContext";
import type { ToolResult } from "./tools";

const SEARCH_MODEL: AgentTaskModel = "anthropic/claude-sonnet-4.6";
export const SEARCH_TIMEOUT_MS = 45_000;

// Structural subset of the SDK's MemorySearchResultItem (not exported from @fencyai/js).
type MemorySearchHit = {
  memoryId: string;
  memoryTitle: string;
  matchingChunk: { pageNumbers: number[] };
  chunks: Array<{ content: string }>;
};

export type DocumentSearchOutcome = { result: ToolResult; sources: DocumentSource[] };

export type DocumentSearch = (query: string, scope: DocumentSearchScope) => Promise<DocumentSearchOutcome>;

type UseDocumentSearchProps = {
  createDocSearchSession: (scope: AgentDocumentSearchScopeDto | null) => Promise<AgentDocumentSearchSessionDto>;
  currentPageEntity: () => PageEntity | null;
};

const SCOPE_TYPES: Partial<Record<PageEntityKind, AgentDocumentScopeTypeDto>> = {
  contract: AgentDocumentScopeTypeDto.Contract,
  v2Event: AgentDocumentScopeTypeDto.SourcingEventV2,
};

const pageScope = (entity: PageEntity | null): AgentDocumentSearchScopeDto | null => {
  const entityType = entity && SCOPE_TYPES[entity.kind];
  return entityType && entity ? { entityType, entityId: entity.id } : null;
};

const bidsScope = (entity: PageEntity | null): AgentDocumentSearchScopeDto | null =>
  entity?.kind === "v2Event"
    ? { entityType: AgentDocumentScopeTypeDto.SourcingEventV2Bids, entityId: entity.id }
    : null;

const failed = (deviationCode: string, message: string): DocumentSearchOutcome => ({
  result: { ok: false, deviationCode, message },
  sources: [],
});

const withTimeout = async <T>(work: Promise<T>, ms: number): Promise<T | "timeout"> => {
  let handle: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<"timeout">((resolve) => {
    handle = setTimeout(() => resolve("timeout"), ms);
  });
  try {
    return await Promise.race([work, timeout]);
  } finally {
    clearTimeout(handle);
  }
};

const plannerHits = (hits: MemorySearchHit[]) => ({
  hits: hits.map((hit) => ({
    document: hit.memoryTitle,
    memoryId: hit.memoryId,
    pages: hit.matchingChunk.pageNumbers,
    excerpt: hit.chunks.map((chunk) => chunk.content).join(""),
  })),
});

const pagesOf = (hits: MemorySearchHit[], memoryId: string): number[] =>
  [...new Set(hits.filter((hit) => hit.memoryId === memoryId).flatMap((hit) => hit.matchingChunk.pageNumbers))].sort(
    (a, b) => a - b,
  );

type ResolveSourcesTrigger = (args: { resolveAgentDocumentSourcesRequest: { memoryIds: string[] } }) => {
  unwrap: () => Promise<AgentDocumentSourcesDto>;
};

// Scope types the cards can navigate to; anything else (or a future enum value) degrades to a
// download-only card instead of a broken link.
const OWNER_TYPES: Partial<Record<string, DocumentSourceOwner["entityType"]>> = {
  CONTRACT: "CONTRACT",
  SOURCING_EVENT_V2: "SOURCING_EVENT_V2",
};

const toOwner = (owner: AgentDocumentOwnerDto | null | undefined): DocumentSourceOwner | undefined => {
  const entityType = owner && OWNER_TYPES[owner.entityType];
  return owner && entityType ? { entityType, entityId: owner.entityId, entityTitle: owner.entityTitle } : undefined;
};

// Best-effort enrichment: a resolution failure degrades the cards, never the search itself.
const resolvedSources = async (
  hits: MemorySearchHit[],
  resolveSources: ResolveSourcesTrigger,
): Promise<DocumentSource[]> => {
  const memoryIds = [...new Set(hits.map((hit) => hit.memoryId))];
  if (memoryIds.length === 0) {
    return [];
  }
  try {
    const { sources } = await resolveSources({ resolveAgentDocumentSourcesRequest: { memoryIds } }).unwrap();
    return sources.map((source) => ({
      memoryId: source.memoryId,
      documentName: source.documentName,
      downloadUrl: source.downloadUrl,
      pages: pagesOf(hits, source.memoryId),
      owner: toOwner(source.owner),
    }));
  } catch {
    return [];
  }
};

export const useDocumentSearch = ({ createDocSearchSession, currentPageEntity }: UseDocumentSearchProps): DocumentSearch => {
  const { createAgentTask } = useAgentTasks({});
  const [resolveSources] = useResolveAgentDocumentSourcesMutation();

  return useCallback(
    async (query, scope) => {
      const trimmed = query.trim();
      if (!trimmed) {
        return failed("EMPTY_QUERY", "Tomt søk.");
      }
      const scopeDto =
        scope === "page" ? pageScope(currentPageEntity()) : scope === "bids" ? bidsScope(currentPageEntity()) : null;
      if (scope === "page" && !scopeDto) {
        return failed(
          "NO_PAGE_ENTITY",
          'Siden brukeren står på har ingen enhet med dokumenter — søk heller med scope "organization".',
        );
      }
      if (scope === "bids" && !scopeDto) {
        return failed(
          "NO_PAGE_ENTITY",
          'Scope "bids" krever at brukeren står på en anskaffelse — søk heller med scope "organization".',
        );
      }
      try {
        const session = await createDocSearchSession(scopeDto);
        const pending = session.pendingDocumentCount ?? 0;
        if (!session.clientToken) {
          return failed(
            "NO_INDEXED_DOCUMENTS",
            pending > 0
              ? `Ingen av dokumentene er søkbare ennå — ${pending} indekseres fortsatt. Prøv igjen om litt.`
              : "Enheten har ingen indekserte dokumenter å søke i.",
          );
        }
        const clientToken = session.clientToken;
        const outcome = await withTimeout(
          createAgentTask(
            {
              type: "MemorySearch",
              query: trimmed,
              model: SEARCH_MODEL,
              language: "no",
              chunkLimit: 5,
              queryExpansion: { queryCount: 3 },
            },
            { fetchCreateAgentTaskClientToken: async () => ({ clientToken }) },
          ),
          SEARCH_TIMEOUT_MS,
        );
        if (outcome === "timeout") {
          return failed("SEARCH_TIMEOUT", "Dokumentsøket brukte for lang tid.");
        }
        if (outcome.type !== "success" || outcome.response.taskType !== "MemorySearch") {
          const message = outcome.type !== "success" ? outcome.error.message : "Uventet svar fra dokumentsøket.";
          return failed("FENCY_ERROR", message);
        }
        const hits = outcome.response.response.items;
        return {
          result: {
            ok: true,
            data: { ...plannerHits(hits), ...(pending > 0 ? { documentsStillIndexing: pending } : {}) },
          },
          sources: await resolvedSources(hits, resolveSources),
        };
      } catch (e) {
        return failed("FENCY_ERROR", (e as Error).message);
      }
    },
    [createAgentTask, createDocSearchSession, currentPageEntity, resolveSources],
  );
};
